import type { RafDocType } from '../../../actions/matters/generateDocument';

/**
 * Pure helpers for resolving which generated S3 files belong to each RAF
 * doc-type card. Extracted from DocumentsTab so the matching logic can be
 * unit-tested without rendering MUI.
 */

// Map a doc-type to a substring that should appear in the generated filename.
export const DOC_MATCHERS: Record<RafDocType, string[]> = {
  'raf-docx-fill': ['RAF1 COMPLETED'],
  'raf-authorisation': ['AUTHORISATION'],
  // The old-form job produces BOTH a PDF ("RAF1 OLD FORM …") and an editable
  // Word doc ("RAF1 OLD FORM WORD …") — both match this substring and both are
  // surfaced as separate downloads.
  'raf-pdf-fill': ['RAF1 OLD FORM'],
  'raf-affidavit': ['AFFIDAVIT'],
  'raf-consultation-note': ['CONSULTATION NOTE'],
  'raf-checklist': ['MVA CHECKLIST'],
};

const EXT_FORMAT_LABEL: Record<string, string> = {
  pdf: 'PDF',
  docx: 'Word',
  doc: 'Word',
  xlsx: 'Excel',
};

export const formatLabelForKey = (key: string): string => {
  const ext = key.split('.').pop()?.toLowerCase() ?? '';
  return EXT_FORMAT_LABEL[ext] ?? ext.toUpperCase();
};

export interface GeneratedFile {
  key: string;
  formatLabel: string;
}

// A doc-type may resolve to more than one generated file (e.g. the old RAF1
// form produces a PDF + an editable Word version). Returns every match, each
// tagged with a human format label derived from its extension.
export const findGeneratedFiles = (
  docType: RafDocType,
  docsGenerated: string[] | undefined,
): GeneratedFile[] => {
  if (!docsGenerated) return [];
  const matchers = DOC_MATCHERS[docType];
  return docsGenerated
    .filter((key) => matchers.some((m) => key.toUpperCase().includes(m.toUpperCase())))
    .map((key) => ({ key, formatLabel: formatLabelForKey(key) }));
};
